import * as React from "react";
import { useNavigate } from "react-router";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { openContractFormWindow } from "./windowManager/ContractFormWindow";
import { displayContactName } from "../utils/contactDisplay";
import {
  Briefcase24Regular,
  ArrowTrendingDown24Regular,
  ShieldTask24Regular,
  VehicleTruck24Regular,
  Scales24Regular,
  VehicleCarProfile24Regular,
  Star24Regular,
  Crown24Regular,
  People24Regular,
  Money24Regular,
  Home24Regular,
  Dismiss20Regular,
  ChevronLeft20Regular,
  ChevronRight16Regular,
  Search20Regular,
  Add20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "./risikoanalyseStep/tokens";
import { apiUrl } from "../utils/api";
import { ContactSelector } from "./ContactSelector";
import { VERTRAGSKATALOG, type Vertragsart, type Vertragskategorie } from "../data/vertragskatalog";
import { kindForArt, type VertragsMaske } from "./neuAnlage/vertragsMaske";

type Kind = VertragsMaske;
type Source = "risk" | "contact";

type RiskRow = {
  id: number;
  name?: string | null;
  riskType?: string | null;
  characteristics?: Array<{ nameBezeichnung?: string; riskCharacteristicValue?: string }>;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

const CATEGORY_ICON: Record<string, React.ComponentType<{ style?: React.CSSProperties }>> = {
  "betriebs-und-anlagevermogen": Briefcase24Regular,
  "ertragsausfall-furs-unternehmen": ArrowTrendingDown24Regular,
  "haftpflichtversicherungen-fur-unternehmen": ShieldTask24Regular,
  "transportversicherungen": VehicleTruck24Regular,
  "rechtsschutzversicherungen": Scales24Regular,
  "kfz-versicherungen": VehicleCarProfile24Regular,
  "spezialversicherungen-fur-finanzielle-risiken": Star24Regular,
  "top-job-management": Crown24Regular,
  "menschen-im-unternehmen": People24Regular,
  "liquiditat": Money24Regular,
  "immobilienprojekte": Home24Regular,
};

const FAVORITES: { artId: string; label: string; icon: React.ComponentType<{ style?: React.CSSProperties }> }[] = [
  // popular shortcuts; ids must match the slug output in vertragskatalog.ts
  { artId: "immobilienprojekte-gebaudeversicherung", label: "Gebäudeversicherung", icon: Home24Regular },
  { artId: "kfz-versicherungen-kfz-versicherung-bausteine-nur-haft-haft-tk-haft-vk-tk-sonstige", label: "KFZ-Versicherung", icon: VehicleCarProfile24Regular },
];


const initials = (name: string) =>
  name
    .split(/\s|,/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase() || "?";

// ── Styles (plain inline-style constants, no makeStyles) ─────────────────

const s = {
  overlay: {
    position: "fixed" as const,
    inset: 0,
    backgroundColor: "rgba(20,8,20,0.55)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 16,
  },
  panel: {
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    width: "min(720px, 100%)",
    maxHeight: "min(640px, 90vh)",
    display: "flex",
    flexDirection: "column" as const,
    borderRadius: 14,
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 24px 60px rgba(20,8,20,0.32)",
    overflow: "hidden",
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    borderBottom: `1px solid ${BRAND.borderDashed}`,
  },
  headLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: BRAND.muted,
    minWidth: 0,
    flex: 1,
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    fontFamily: BRAND.fontAccent,
    color: BRAND.ink,
  },
  crumbBtn: {
    background: "none",
    border: "none",
    padding: 0,
    color: BRAND.muted,
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  crumbCurrent: {
    color: BRAND.ink,
    fontWeight: 600,
  },
  closeBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "none",
    backgroundColor: "transparent",
    color: BRAND.muted,
    cursor: "pointer",
  },
  body: {
    padding: 16,
    overflowY: "auto" as const,
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: 14,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    color: BRAND.muted,
    marginBottom: 6,
  },
  favRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 8,
  },
  favPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 999,
    border: `1px solid ${BRAND.border}`,
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  favPillIcon: {
    width: 30,
    height: 30,
    borderRadius: 999,
    backgroundColor: "#f5f1ee",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: BRAND.ink,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
  },
  tile: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: 8,
    padding: "12px 12px",
    minHeight: 86,
    backgroundColor: BRAND.white,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 10,
    cursor: "pointer",
    textAlign: "left" as const,
    color: BRAND.ink,
    fontFamily: "inherit",
    transition: "border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease",
  },
  tileIcon: {
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: "#f5f1ee",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: BRAND.ink,
  },
  tileLabel: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.25,
  },
  artList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 4,
  },
  artRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px",
    backgroundColor: BRAND.white,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 8,
    cursor: "pointer",
    textAlign: "left" as const,
    color: BRAND.ink,
    fontFamily: "inherit",
  },
  artMain: { display: "flex", flexDirection: "column" as const, gap: 2, minWidth: 0 },
  artName: { fontSize: 13, fontWeight: 600 },
  artDetail: { fontSize: 11, color: BRAND.muted },
  sourceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },
  sourceTile: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
    padding: 14,
    backgroundColor: BRAND.white,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 10,
    cursor: "pointer",
    textAlign: "left" as const,
    color: BRAND.ink,
    fontFamily: "inherit",
  },
  sourceTitle: { fontSize: 14, fontWeight: 600 },
  sourceHint: { fontSize: 12, color: BRAND.muted, lineHeight: 1.35 },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 8,
    backgroundColor: BRAND.white,
  },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    fontSize: 13,
    fontFamily: "inherit",
    color: BRAND.ink,
  },
  riskRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    backgroundColor: BRAND.white,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 8,
    cursor: "pointer",
    textAlign: "left" as const,
    color: BRAND.ink,
    fontFamily: "inherit",
  },
  riskBadge: {
    width: 32,
    height: 32,
    borderRadius: 999,
    backgroundColor: BRAND.gold,
    color: BRAND.ink,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 12,
    flexShrink: 0,
  },
  riskBody: { display: "flex", flexDirection: "column" as const, minWidth: 0, gap: 2 },
  riskName: { fontSize: 13, fontWeight: 600 },
  riskAddr: { fontSize: 11, color: BRAND.muted },
  hint: {
    fontSize: 12,
    color: BRAND.muted,
    padding: "12px 4px",
  },
  list: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
  },
  ctaRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    backgroundColor: "#f5f1ee",
    border: `1px dashed ${BRAND.border}`,
    borderRadius: 8,
    cursor: "pointer",
    color: BRAND.ink,
    fontFamily: "inherit",
    fontWeight: 600,
    fontSize: 13,
  },
};

// ── Component ────────────────────────────────────────────────────────────

const NewContractDialog: React.FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { openWindow } = useWindowManager();

  const [katId, setKatId] = React.useState<string | null>(null);
  const [art, setArt] = React.useState<Vertragsart | null>(null);
  const [source, setSource] = React.useState<Source | null>(null);
  const [contactId, setContactId] = React.useState<number | null>(null);
  const [contactName, setContactName] = React.useState<string | null>(null);
  const [risks, setRisks] = React.useState<RiskRow[] | null>(null);
  const [loadingRisks, setLoadingRisks] = React.useState(false);
  const [search, setSearch] = React.useState("");

  // reset on open
  React.useEffect(() => {
    if (open) {
      setKatId(null);
      setArt(null);
      setSource(null);
      setContactId(null);
      setContactName(null);
      setRisks(null);
      setSearch("");
    }
  }, [open]);

  // lock background scroll
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const kat: Vertragskategorie | null = React.useMemo(
    () => (katId ? VERTRAGSKATALOG.find((k) => k.id === katId) ?? null : null),
    [katId],
  );
  const kind: Kind | null = art ? kindForArt(art) : null;

  // Load all risks for risk-source (immo/kfz) or contact-immo sub-step.
  React.useEffect(() => {
    // case A: source=risk → load all risks of the contact's kind
    // case B: contact picked & immo → load risks of that contact
    if (!open) return;
    if (!art) return;
    const wantsAllRisks = source === "risk" && (kind === "immobilie" || kind === "kfz");
    const wantsContactImmoRisks = source === "contact" && contactId != null && kind === "immobilie";
    if (!wantsAllRisks && !wantsContactImmoRisks) return;

    let cancelled = false;
    setLoadingRisks(true);
    (async () => {
      try {
        const url = wantsContactImmoRisks
          ? apiUrl(`risk-objects/by-contact/${contactId}`)
          : apiUrl("risk-objects", {
              q: kind === "immobilie" ? "Immobilie" : "KFZ",
              limit: "200",
            });
        const res = await fetch(url);
        const data = res.ok ? ((await res.json()) as RiskRow[]) : [];
        const wanted = (Array.isArray(data) ? data : []).filter((r) => {
          const t = (r.riskType ?? "").toLowerCase();
          return kind === "immobilie" ? t.includes("immo") : t.includes("kfz");
        });
        if (!cancelled) setRisks(wanted);
      } catch {
        if (!cancelled) setRisks([]);
      } finally {
        if (!cancelled) setLoadingRisks(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, art, source, kind, contactId]);

  const filteredRisks = React.useMemo(() => {
    if (!risks) return [] as RiskRow[];
    const q = search.trim().toLowerCase();
    if (!q) return risks;
    const charVal = (r: RiskRow, key: string) =>
      String(
        (r.characteristics ?? []).find((c) => c.nameBezeichnung === key)?.riskCharacteristicValue ?? "",
      ).toLowerCase();
    return risks.filter((r) => {
      const name = (r.name ?? "").toLowerCase();
      if (kind === "kfz") {
        return (
          name.includes(q) ||
          charVal(r, "licensePlate").includes(q) ||
          charVal(r, "manufacturer").includes(q) ||
          charVal(r, "typeName").includes(q) ||
          charVal(r, "vin").includes(q)
        );
      }
      return (
        name.includes(q) || charVal(r, "street").includes(q) || charVal(r, "city").includes(q)
      );
    });
  }, [risks, search, kind]);

  const pickArt = (a: Vertragsart) => {
    setArt(a);
    setRisks(null);
    setSearch("");
    const k = kindForArt(a);
    // Generic: only the contact path makes sense — skip the source picker.
    setSource(k === "generic" || k === "transport" ? "contact" : null);
  };

  const goRiskFlow = (riskId: number, riskRow?: RiskRow) => {
    onClose();
    if (kind === "kfz" || kind === "immobilie") {
      openContractFormWindow(openWindow, {
        variant: kind,
        contactId: contactId ?? null,
        contactName: contactName ?? riskRow?.name ?? null,
        riskObjectId: riskId,
      });
      return;
    }
    // generic fallback (kein Window-Adapter v1)
    const contactQuery = contactId ? `?contactId=${contactId}` : "";
    navigate(`/risks/${riskId}/contracts/new${contactQuery}`);
  };

  const goContactFlow = (cid: number, name?: string | null) => {
    if (kind === "kfz") {
      onClose();
      openContractFormWindow(openWindow, {
        variant: "kfz",
        contactId: cid,
        contactName: name ?? contactName,
      });
      return;
    }
    if (kind === "transport") {
      // Transport-Verträge hängen an keinem Risikoobjekt: die Maske führt die
      // fünf Vertragsarten selbst und braucht nur den Versicherungsnehmer.
      onClose();
      navigate("/vertrag/transport", { state: { kontaktId: cid } });
      return;
    }
    if (kind === "generic") {
      // Generic contracts: go directly to the Allgemein-Vertragsmaske with the
      // chosen contact pre-loaded. No intermediate Immobilien-Risikoauswahl.
      // Pass the selected Vertragsart name so the page can switch into matrix
      // mode for Inhalts-/Ertragsausfallversicherungen.
      onClose();
      navigate("/vertrag/new", {
        state: {
          kontaktId: cid,
          artName: art?.name ?? null,
          kategorieId: kat?.id ?? null,
        },
      });
      return;
    }
    // Immobilie: open sub-step to pick an Immobilien-Risikoobjekt of this contact.
    setContactId(cid);
  };

  const openNewImmoRiskForContact = (cid: number) => {
    onClose();
    navigate(`/risks/new?contactId=${cid}`);
  };

  if (!open) return null;

  // Determine current step for breadcrumb & body.
  const showSourcePick =
    !!art && kind !== "generic" && kind !== "transport" && source == null;
  const showRiskPicker = !!art && source === "risk";
  const showContactPicker = !!art && source === "contact" && contactId == null;
  const showContactImmoSub = !!art && source === "contact" && contactId != null && kind === "immobilie";
  const showCategoryGrid = !kat && !art;
  const showArtList = !!kat && !art;

  return (
    <div
      style={s.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Neuer Vertrag"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={s.panel}>
        <div style={s.head}>
          <div style={s.headLeft}>
            <h2 style={s.title}>Neuer Vertrag</h2>
            {(kat || art || source) && (
              <>
                <ChevronRight16Regular style={{ color: BRAND.muted }} />
                <button
                  type="button"
                  style={s.crumbBtn}
                  onClick={() => {
                    setKatId(null);
                    setArt(null);
                    setSource(null);
                    setContactId(null);
                  }}
                >
                  Kategorie
                </button>
              </>
            )}
            {kat && !art && (
              <>
                <ChevronRight16Regular style={{ color: BRAND.muted }} />
                <span style={s.crumbCurrent}>{kat.name}</span>
              </>
            )}
            {art && (
              <>
                <ChevronRight16Regular style={{ color: BRAND.muted }} />
                <button
                  type="button"
                  style={s.crumbBtn}
                  onClick={() => {
                    setArt(null);
                    setSource(null);
                    setContactId(null);
                  }}
                >
                  {art.name}
                </button>
              </>
            )}
            {source && (
              <>
                <ChevronRight16Regular style={{ color: BRAND.muted }} />
                <span style={s.crumbCurrent}>
                  {source === "risk" ? "Aus Risiko" : "Aus Kontakt"}
                </span>
              </>
            )}
          </div>
          <button type="button" style={s.closeBtn} onClick={onClose} aria-label="Schließen">
            <Dismiss20Regular />
          </button>
        </div>

        <div style={s.body}>
          {/* Step 1a: Categories + favorites */}
          {showCategoryGrid && (
            <>
              <div>
                <div style={s.sectionLabel}>Beliebt</div>
                <div style={s.favRow}>
                  {FAVORITES.map((f) => {
                    const found = findArt(f.artId);
                    if (!found) return null;
                    const Icon = f.icon;
                    return (
                      <button
                        key={f.artId}
                        type="button"
                        style={s.favPill}
                        onClick={() => pickArt(found.art)}
                      >
                        <span style={s.favPillIcon}>
                          <Icon />
                        </span>
                        {f.label}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    style={s.favPill}
                    onClick={() => {
                      onClose();
                      navigate("/vertrag/new", { state: { openAllgemein: true } });
                    }}
                    title="Allgemeines Vertragsformular — Kontakt wird im nächsten Schritt gewählt"
                  >
                    <span style={s.favPillIcon}>
                      <Briefcase24Regular />
                    </span>
                    Allgemein Vertrag anlegen
                  </button>
                </div>
              </div>
              <div>
                <div style={s.sectionLabel}>Vertragskategorie</div>
                <div style={s.grid}>
                  {VERTRAGSKATALOG.map((k) => {
                    const Icon = CATEGORY_ICON[k.id] ?? Briefcase24Regular;
                    return (
                      <button
                        key={k.id}
                        type="button"
                        style={s.tile}
                        onClick={() => setKatId(k.id)}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.borderColor = BRAND.purple;
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.borderColor = BRAND.border;
                        }}
                      >
                        <span style={s.tileIcon}>
                          <Icon />
                        </span>
                        <span style={s.tileLabel}>{k.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Step 1b: Vertragsarten of the chosen Kategorie */}
          {showArtList && kat && (
            <>
              <button
                type="button"
                style={{ ...s.crumbBtn, color: BRAND.ink, display: "inline-flex", alignItems: "center", gap: 4 }}
                onClick={() => setKatId(null)}
              >
                <ChevronLeft20Regular /> Zurück zu Kategorien
              </button>
              <div style={s.sectionLabel}>Versicherung wählen</div>
              <div style={s.artList}>
                {kat.arten.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    style={s.artRow}
                    onClick={() => pickArt(a)}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.borderColor = BRAND.purple;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.borderColor = BRAND.border;
                    }}
                  >
                    <span style={s.artMain}>
                      <span style={s.artName}>{a.name}</span>
                      {a.detail && <span style={s.artDetail}>{a.detail}</span>}
                    </span>
                    <ChevronRight16Regular style={{ color: BRAND.muted }} />
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step 2: Source pick (only for immobilie/kfz) */}
          {showSourcePick && art && (
            <>
              <div style={s.sectionLabel}>Quelle wählen</div>
              <div style={s.sourceGrid}>
                <button type="button" style={s.sourceTile} onClick={() => setSource("risk")}>
                  <div style={s.sourceTitle}>Aus Risiko</div>
                  <div style={s.sourceHint}>
                    Bestehendes Risikoobjekt wählen — Halter und Stammdaten werden übernommen.
                  </div>
                </button>
                <button type="button" style={s.sourceTile} onClick={() => setSource("contact")}>
                  <div style={s.sourceTitle}>Aus Kontakt</div>
                  <div style={s.sourceHint}>
                    Versicherungsnehmer zuerst wählen — Risiko anschließend.
                  </div>
                </button>
              </div>
            </>
          )}

          {/* Step 3a: Risk picker (source=risk) */}
          {showRiskPicker && (
            <>
              <div style={s.searchRow}>
                <Search20Regular style={{ color: BRAND.muted }} />
                <input
                  style={s.searchInput}
                  type="search"
                  placeholder={
                    kind === "kfz"
                      ? "Suche nach Kennzeichen, Marke, Modell oder VIN…"
                      : "Suche nach Name, Straße oder Ort…"
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {loadingRisks ? (
                <div style={s.hint}>Lade Risiken…</div>
              ) : filteredRisks.length === 0 ? (
                <div style={s.hint}>Keine passenden Risiken gefunden.</div>
              ) : (
                <div style={s.list}>
                  {filteredRisks.map((r) => (
                    <RiskRowView key={r.id} r={r} kind={kind!} onClick={() => goRiskFlow(r.id, r)} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Step 3b: Contact picker (source=contact) */}
          {showContactPicker && (
            <ContactSelector
              label="Versicherungsnehmer / Halter"
              placeholder="Kontakt suchen (Name, Firma)…"
              onSelect={(c) => {
                if (!c?.id) return;
                const name = displayContactName(c);
                setContactName(name);
                goContactFlow(Number(c.id), name);
              }}
            />
          )}

          {/* Step 3c (Immobilie only): pick / create Immobilien-Risiko for the chosen contact */}
          {showContactImmoSub && (
            <>
              <div style={s.sectionLabel}>Immobilien-Risiko des Kontakts</div>
              {loadingRisks ? (
                <div style={s.hint}>Lade Risiken…</div>
              ) : filteredRisks.length === 0 ? (
                <div style={s.hint}>
                  Dieser Kontakt hat noch kein Immobilien-Risiko. Legen Sie zunächst eines an.
                </div>
              ) : (
                <div style={s.list}>
                  {filteredRisks.map((r) => (
                    <RiskRowView key={r.id} r={r} kind="immobilie" onClick={() => goRiskFlow(r.id, r)} />
                  ))}
                </div>
              )}
              <button
                type="button"
                style={s.ctaRow}
                onClick={() => openNewImmoRiskForContact(contactId!)}
              >
                <Add20Regular />
                Neues Immobilien-Risiko anlegen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const findArt = (id: string): { kat: Vertragskategorie; art: Vertragsart } | null => {
  for (const k of VERTRAGSKATALOG) {
    const a = k.arten.find((x) => x.id === id);
    if (a) return { kat: k, art: a };
  }
  return null;
};

const RiskRowView: React.FC<{ r: RiskRow; kind: Kind; onClick: () => void }> = ({ r, kind, onClick }) => {
  const charVal = (key: string) =>
    String(
      (r.characteristics ?? []).find((c) => c.nameBezeichnung === key)?.riskCharacteristicValue ?? "",
    );
  if (kind === "kfz") {
    const plate = charVal("licensePlate");
    const manuf = charVal("manufacturer");
    const model = charVal("typeName");
    const name = r.name || (plate ? plate : `KFZ ${r.id}`);
    const sub = [plate && plate !== name ? plate : null, [manuf, model].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(" · ");
    return (
      <button type="button" style={s.riskRow} onClick={onClick}>
        <span style={s.riskBadge}>{initials(name)}</span>
        <span style={s.riskBody}>
          <span style={s.riskName}>{name}</span>
          <span style={s.riskAddr}>{sub || "—"}</span>
        </span>
      </button>
    );
  }
  const street = charVal("street");
  const plz = charVal("postalCode");
  const city = charVal("city");
  const name = r.name || `Risiko ${r.id}`;
  const addr = [street, [plz, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return (
    <button type="button" style={s.riskRow} onClick={onClick}>
      <span style={s.riskBadge}>{initials(name)}</span>
      <span style={s.riskBody}>
        <span style={s.riskName}>{name}</span>
        <span style={s.riskAddr}>{addr || "—"}</span>
      </span>
    </button>
  );
};

export default NewContractDialog;
